import { z } from 'zod'
import FileSchema from 'features/declaration/models/schemas/fileSchemas'
import { CreateForm, UpdateForm } from 'shared/rhf/models/types'

export const DocumentTypeEnum = z.enum([
  'TRANSPORT',
  'PREVIOUS',
  'SUPPORTING',
  'NOTE',
])
export type DocumentTypeEnumType = z.infer<typeof DocumentTypeEnum>

const BaseDocumentSchema = z.object(
  {
    id: z.number().nullable(),
    type: DocumentTypeEnum,
    sequenceNumber: z.number().nonnegative(),
    documentType: z.string(),
    referenceNumber: z.string(),
    complementOfInformation: z.string(),
    deleted: z.boolean().optional(),
  },
)
export type BaseDocument = z.infer<typeof BaseDocumentSchema>

export const PreviousDocumentSchema = BaseDocumentSchema.extend(
  {
    transportDocumentReferenceNumber: z.string(),
    procedureType: z.string().nullable(),
    files: z.array(FileSchema),
  },
) // eslint-disable-next-line @typescript-eslint/no-unused-vars
  .superRefine((arg, ctx) => {})
export type PreviousDocument = z.infer<typeof PreviousDocumentSchema>

export const SupportingDocumentSchema = BaseDocumentSchema.extend(
  {
    documentLineNumber: z.number().nullable(),
    files: z.array(FileSchema),
  },
// eslint-disable-next-line @typescript-eslint/no-unused-vars
).superRefine((arg, ctx) => {})
export type SupportingDocument = z.infer<typeof SupportingDocumentSchema>

export const TransportDocumentSchema = BaseDocumentSchema.omit(
  {
    complementOfInformation: true,
  },
).extend({
  files: z.array(FileSchema),
})
// eslint-disable-next-line @typescript-eslint/no-unused-vars
  .superRefine((arg, ctx) => {})
export type TransportDocument = z.infer<typeof TransportDocumentSchema>
export type TransportDocumentData = CreateForm<TransportDocument> | UpdateForm<TransportDocument>
export type SupportingDocumentData = CreateForm<SupportingDocument> | UpdateForm<SupportingDocument>
export type PreviousDocumentData = CreateForm<PreviousDocument> | UpdateForm<PreviousDocument>

export const NotesDocumentSchema = BaseDocumentSchema.extend({
  files: z.array(FileSchema),
  transitOperationId: z.number().nullable(),
})

export type NotesDocument = z.infer<typeof NotesDocumentSchema>

export type DocumentFormValues = PreviousDocument | SupportingDocument | TransportDocument | NotesDocument

export type DocumentData = CreateForm<DocumentFormValues> | UpdateForm<DocumentFormValues>
export type DocumentCreateData = CreateForm<DocumentFormValues>
export type DocumentUpdateData = UpdateForm<DocumentFormValues>
export type DocumentAnyData = DocumentCreateData | DocumentUpdateData

export type TransportDocumentCreateFormValues = Extract<DocumentCreateData, { type: 'TRANSPORT' }>
export type PreviousDocumentCreateFormValues = Extract<DocumentCreateData, { type: 'PREVIOUS' }>
export type SupportingDocumentCreateFormValues = Extract<DocumentCreateData, { type: 'SUPPORTING' }>
export type NoteDocumentCreateFormValues = Extract<DocumentCreateData, { type: 'NOTE' }>
export type TransportDocumentUpdateFormValues = Extract<DocumentUpdateData, { type: 'TRANSPORT' }>
export type PreviousDocumentUpdateFormValues = Extract<DocumentUpdateData, { type: 'PREVIOUS' }>
export type SupportingDocumentUpdateFormValues = Extract<DocumentUpdateData, { type: 'SUPPORTING' }>
export type NoteDocumentUpdateFormValues = Extract<DocumentUpdateData, { type: 'NOTE' }>

export function isCreateDocumentValues(document: DocumentAnyData): document is DocumentCreateData {
  return document.id === null
}

export function isUpdateDocumentValues(document: DocumentAnyData): document is DocumentUpdateData {
  return document.id !== null
}
